/*
  Product page gallery carousel (main-product setting "gallery_carousel").
  - Desktop: one big image at a time, product-card style arrows (loop), thumbnail row below;
    the current thumbnail gets the 2px line.
  - Mobile: native swipe + thin progress bar (same as the New In / IG carousels).
  The slide list is Dawn's slider (scroll-snap). This file only moves its scroll position and keeps
  arrows / thumbnails / progress in sync. On variant change product-info.js swaps the slides and
  .pg-thumbs; the MutationObserver below then resets to the first slide.
*/
(() => {
  const BUILD = '2026-10-22-v1';
  console.log(`[pg-carousel] build ${BUILD}`);

  const DURATION = 400; // = product-card gallery slide (0.4s ease-in-out)
  const easeInOut = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class GalleryCarousel {
    constructor(gallery) {
      this.gallery = gallery;
      this.list = gallery.querySelector('.product__media-list');
      this.thumbs = gallery.querySelector('.pg-thumbs');
      this.fill = gallery.querySelector('.pg-progress__fill');
      if (!this.list) return;

      this.current = 0;
      this.tweening = false;
      this.ticking = false;

      this.list.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
      gallery.addEventListener('click', (event) => this.onClick(event));
      new MutationObserver(() => this.onSlidesChanged()).observe(this.list, { childList: true });
      if (this.thumbs) new MutationObserver(() => this.update()).observe(this.thumbs, { childList: true });
      window.addEventListener('resize', () => this.requestUpdate());

      this.update();
    }

    slides() {
      return Array.from(this.list.querySelectorAll(':scope > li[data-media-id]'));
    }

    // scroll position of a slide, measured from the first slide (ignores list padding / peek margin)
    slideLeft(slides, index) {
      return slides[index].offsetLeft - slides[0].offsetLeft;
    }

    indexFromScroll(slides) {
      const x = this.list.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      slides.forEach((_, i) => {
        const distance = Math.abs(this.slideLeft(slides, i) - x);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    requestUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    update() {
      const slides = this.slides();
      const count = slides.length;
      this.gallery.classList.toggle('pg-single', count < 2);
      if (!count) return;

      if (!this.tweening) this.current = this.indexFromScroll(slides);
      this.current = Math.min(this.current, count - 1);
      const activeId = slides[this.current].dataset.mediaId;

      if (this.thumbs) {
        this.thumbs.querySelectorAll('.pg-thumb').forEach((thumb) => {
          const isCurrent = thumb.dataset.target === activeId;
          thumb.classList.toggle('is-current', isCurrent);
          if (isCurrent) {
            thumb.setAttribute('aria-current', 'true');
            this.keepThumbInView(thumb);
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }

      if (this.fill) {
        const total = this.list.scrollWidth;
        const ratio = total > 0 ? (this.list.scrollLeft + this.list.clientWidth) / total : 1;
        this.fill.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
      }
    }

    // only matters when there are more thumbnails than fit in one row
    keepThumbInView(thumb) {
      const row = this.thumbs;
      if (row.scrollWidth <= row.clientWidth) return;
      const left = thumb.offsetLeft;
      const right = left + thumb.offsetWidth;
      if (left >= row.scrollLeft && right <= row.scrollLeft + row.clientWidth) return;
      row.scrollTo({ left: left - (row.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
    }

    onClick(event) {
      const nav = event.target.closest('.pg-nav');
      if (nav) {
        event.preventDefault();
        const count = this.slides().length;
        if (count < 2) return;
        const next = this.current + (nav.classList.contains('pg-nav--next') ? 1 : -1);
        // loop like the product-card gallery; the wrap-around jumps instead of sweeping back across every slide
        if (next < 0 || next >= count) this.goTo((next + count) % count, false);
        else this.goTo(next, true);
        return;
      }

      const thumb = event.target.closest('.pg-thumb');
      if (thumb) {
        event.preventDefault();
        const index = this.slides().findIndex((slide) => slide.dataset.mediaId === thumb.dataset.target);
        if (index > -1) this.goTo(index, true);
      }
    }

    goTo(index, animate) {
      const slides = this.slides();
      if (!slides[index]) return;
      this.stopTween();
      this.current = index;

      const maxLeft = this.list.scrollWidth - this.list.clientWidth;
      const target = Math.max(0, Math.min(this.slideLeft(slides, index), maxLeft));
      const start = this.list.scrollLeft;
      const delta = target - start;

      if (!animate || reducedMotion.matches || Math.abs(delta) < 1) {
        this.jump(target);
        this.update();
        return;
      }

      // tween scrollLeft ourselves (native smooth scroll has no fixed duration);
      // snapping is paused so it doesn't fight the intermediate positions
      this.tweening = true;
      this.list.style.scrollSnapType = 'none';
      this.list.style.scrollBehavior = 'auto';
      this.update();

      const startTime = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - startTime) / DURATION);
        this.list.scrollLeft = start + delta * easeInOut(t);
        if (t < 1) {
          this.raf = requestAnimationFrame(step);
          return;
        }
        this.stopTween();
        this.update();
      };
      this.raf = requestAnimationFrame(step);
    }

    stopTween() {
      cancelAnimationFrame(this.raf);
      this.tweening = false;
      this.list.style.scrollSnapType = '';
      this.list.style.scrollBehavior = '';
    }

    jump(left) {
      this.list.style.scrollBehavior = 'auto';
      this.list.scrollLeft = left;
      this.list.style.scrollBehavior = '';
    }

    // variant change: product-info.js swapped the slides (and .pg-thumbs) — match order, back to slide 1
    onSlidesChanged() {
      if (this.thumbs) {
        const thumbsById = new Map(
          Array.from(this.thumbs.querySelectorAll('.pg-thumb')).map((thumb) => [thumb.dataset.target, thumb])
        );
        this.slides().forEach((slide) => {
          const thumb = thumbsById.get(slide.dataset.mediaId);
          if (thumb) this.thumbs.appendChild(thumb);
        });
      }
      this.stopTween();
      this.current = 0;
      this.jump(0);
      this.update();
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('media-gallery.pg-carousel').forEach((gallery) => {
      if (gallery.dataset.pgInit) return;
      gallery.dataset.pgInit = 'true';
      new GalleryCarousel(gallery);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
